//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useCallback, useEffect } from 'react';
import { useConfig } from '../../Config';
import { DropdownMenuItem } from '../ui/dropdown-menu';
import { getRoutePath, solutionsApp } from '../../routeUtils';
import { useUserAuth } from '../../UserAuth';
import { useActiveLoopContext } from '../../ActiveLoopContext';
import { useTeamspaceContext } from '../../TeamspaceContext';
import { buildUrlWithLoopContext } from '../../utils/loopContextUtils';
import loopLogoDarkMode from '../../icons/modern/loop_logo.png';
import planSwitcher from '../../icons/modern/plan_switcher.svg';
import buildSwitcher from '../../icons/modern/build_switcher.svg';
import testSwitcher from '../../icons/modern/test_switcher.svg';
import controlSwitcher from '../../icons/modern/control_switcher.svg';
import codeSwitcher from '../../icons/modern/code_switcher.svg';
import deploySwitcher from '../../icons/modern/deploy_switcher.svg';
import measureSwitcher from '../../icons/modern/measure_switcher.svg';
import modelSwitcher from '../../icons/modern/model_switcher.svg';
import appScanLogo from '../../icons/modern/appscan_logo.png';
import harborLogo from '../../icons/modern/harbor_logo.png';
import { isSafeRedirectUrl } from '../../utils/validateUrlUtils';
import { useTranslation } from 'react-i18next';

interface DropdownItemProps {
  to: string;
  imgAlt: string;
  name: string;
  text: string;
  icon: any;
  iconType?: 'svg' | 'carbon';
  isAvailable: boolean;
  isExternal?: boolean;
  loopId?: string | null;
  loopName?: string | null;
  teamspaceId?: string | null;
}

const DropdownItem: React.FC<DropdownItemProps> = ({
  to,
  imgAlt,
  text,
  icon: IconComponent,
  iconType = 'svg',
  name,
  isAvailable,
  isExternal = false,
  loopId,
  loopName,
  teamspaceId,
}) => {
  const { baseName } = useConfig();
  const handleSelect = useCallback(() => {
    if (!isAvailable || !to) return;

    let targetUrl = to;

    // Always append teamspaceId for internal apps; append loop params only when active
    if (!isExternal && teamspaceId) {
      targetUrl = buildUrlWithLoopContext(to, teamspaceId, loopId, loopName);
    }

    if (isExternal) {
      // External URLs come from server-side config (trusted); only require https.
      if (targetUrl.startsWith('https://')) {
        window.open(targetUrl, '_blank', 'noopener,noreferrer');
      } else {
        console.error('Blocked non-https external link:', targetUrl);
      }
    } else {
      const fullUrl = `${baseName}${targetUrl}`;
      if (isSafeRedirectUrl(fullUrl)) {
        window.open(fullUrl, '_blank', 'noopener,noreferrer');
      } else {
        console.error('Blocked unsafe link:', fullUrl);
      }
    }
  }, [
    isAvailable,
    baseName,
    to,
    isExternal,
    loopId,
    loopName,
    teamspaceId,
    name,
  ]);

  return (
    <DropdownMenuItem
      id={`dropdown-item-${name.toLowerCase().replace(/\s+/g, '-')}`}
      disabled={!isAvailable}
      onSelect={handleSelect}
      className={`flex flex-col items-center gap-1 rounded border-none p-2 transition-colors duration-200 ${
        isAvailable
          ? 'cursor-pointer opacity-100 hover:bg-gray-100 dark:hover:bg-gray-700'
          : 'cursor-default opacity-60'
      }`}
    >
      {iconType === 'svg' ? (
        <img src={IconComponent} alt={imgAlt} className="size-6" />
      ) : (
        <IconComponent size={24} className="text-indigo-500" />
      )}
      <span
        className="text-xs font-semibold text-gray-700 dark:text-gray-300"
        id={`id-dropdown-item-label-${name.toLowerCase().replace(/\s+/g, '-')}`}
        data-testid={`dropdown-item-label-${name.toLowerCase().replace(/\s+/g, '-')}`}
      >
        {text}
      </span>
    </DropdownMenuItem>
  );
};

const DropdownMenu: React.FC = () => {
  const { solutions, redirectUrl, services } = useConfig();
  const { t } = useTranslation();
  const { licenses } = useUserAuth();
  const {
    activeLoopId,
    activeLoopName,
    activeTeamspaceId,
    isLoopContextActive,
  } = useActiveLoopContext();
  const { teamspaceId: teamspaceCtxId } = useTeamspaceContext();
  const effectiveTeamspaceId = activeTeamspaceId || teamspaceCtxId || null;
  const LicenseApp = licenses.flatMap((license) =>
    license.split(',').map((item: string) => item.trim().toLowerCase()),
  );

  const solutionsArray = Array.isArray(solutions) ? solutions : [];

  const solutionItems = [
    {
      label: 'Plan',
      icon: planSwitcher,
      iconType: 'svg' as const,
      href: solutionsArray.find((s: any) => s.name === 'Plan' && s.show)
        ? getRoutePath(solutionsArray as solutionsApp[], 'Plan')
        : undefined,
      isAvailable:
        !!solutionsArray.find((s: any) => s.name === 'Plan' && s.show) &&
        LicenseApp.includes('plan'),
    },
    {
      label: 'Code',
      icon: codeSwitcher,
      iconType: 'svg' as const,
      href: solutionsArray.find((s: any) => s.name === 'Code' && s.show)
        ? getRoutePath(solutionsArray as solutionsApp[], 'Code')
        : undefined,
      isAvailable:
        !!solutionsArray.find((s: any) => s.name === 'Code' && s.show) &&
        LicenseApp.includes('code'),
    },
    {
      label: 'Test',
      icon: testSwitcher,
      iconType: 'svg' as const,
      href: solutionsArray.find((s: any) => s.name === 'Test' && s.show)
        ? getRoutePath(solutionsArray as solutionsApp[], 'Test')
        : undefined,
      isAvailable:
        !!solutionsArray.find((s: any) => s.name === 'Test' && s.show) &&
        LicenseApp.includes('test'),
    },
    {
      label: 'Model',
      icon: modelSwitcher,
      iconType: 'svg' as const,
      href: solutionsArray.find((s: any) => s.name === 'Model' && s.show)
        ? getRoutePath(solutionsArray as solutionsApp[], 'Model')
        : undefined,
      isAvailable:
        !!solutionsArray.find((s: any) => s.name === 'Model' && s.show) &&
        LicenseApp.includes('model'),
    },
    {
      label: 'Build',
      icon: buildSwitcher,
      iconType: 'svg' as const,
      href: solutionsArray.find((s: any) => s.name === 'Build' && s.show)
        ? getRoutePath(solutionsArray as solutionsApp[], 'Build')
        : undefined,
      isAvailable:
        !!solutionsArray.find((s: any) => s.name === 'Build' && s.show) &&
        LicenseApp.includes('build'),
    },
    {
      label: 'Release',
      icon: measureSwitcher,
      iconType: 'svg' as const,
      href: solutionsArray.find((s: any) => s.name === 'Release' && s.show)
        ? getRoutePath(solutionsArray as solutionsApp[], 'Release')
        : undefined,
      isAvailable:
        !!solutionsArray.find((s: any) => s.name === 'Release' && s.show) &&
        LicenseApp.includes('release'),
    },
    {
      label: 'Control',
      icon: controlSwitcher,
      iconType: 'svg' as const,
      href: solutionsArray.find((s: any) => s.name === 'Control' && s.show)
        ? getRoutePath(solutionsArray as solutionsApp[], 'Control')
        : undefined,
      isAvailable:
        !!solutionsArray.find((s: any) => s.name === 'Control' && s.show) &&
        LicenseApp.includes('control'),
    },
    {
      label: 'Deploy',
      icon: deploySwitcher,
      iconType: 'svg' as const,
      href: solutionsArray.find((s: any) => s.name === 'Deploy' && s.show)
        ? getRoutePath(solutionsArray as solutionsApp[], 'Deploy')
        : undefined,
      isAvailable:
        !!solutionsArray.find((s: any) => s.name === 'Deploy' && s.show) &&
        LicenseApp.includes('deploy'),
    },
    {
      label: 'Measure',
      icon: measureSwitcher,
      iconType: 'svg' as const,
      href: solutionsArray.find((s: any) => s.name === 'Measure' && s.show)
        ? getRoutePath(solutionsArray as solutionsApp[], 'Measure')
        : undefined,
      isAvailable:
        !!solutionsArray.find((s: any) => s.name === 'Measure' && s.show) &&
        LicenseApp.includes('measure'),
    },
    ...(services?.provider?.name && services.provider.name !== 'IBM'
      ? [
          {
            label: 'AppScan',
            icon: appScanLogo,
            iconType: 'svg' as const,
            href: solutionsArray.find(
              (s: any) => s.name === 'AppScan' && s.show,
            )
              ? solutionsArray.find((s: any) => s.name === 'AppScan')?.path
              : undefined,
            isAvailable:
              !!solutionsArray.find(
                (s: any) => s.name === 'AppScan' && s.show,
              ) && LicenseApp.includes('appscan'),
            isExternal: true,
          },
        ]
      : []),
    {
      label: 'Harbor',
      icon: harborLogo,
      iconType: 'svg' as const,
      href: solutionsArray.find((s: any) => s.name === 'Harbor' && s.show)
        ? solutionsArray.find((s: any) => s.name === 'Harbor')?.path
        : undefined,
      isAvailable:
        !!solutionsArray.find((s: any) => s.name === 'Harbor' && s.show) &&
        LicenseApp.includes('harbor'),
      isExternal: true,
    },
  ];

  const handleLoopHomeSelect = useCallback(() => {
    if (!redirectUrl) return;
    if (isSafeRedirectUrl(redirectUrl)) {
      window.open(redirectUrl, '_blank', 'noopener,noreferrer');
    } else {
      console.error('Blocked unsafe redirect URL:', redirectUrl);
    }
  }, [redirectUrl]);

  return (
    <div id="id-solutions-dropdown" data-testid="solutions-dropdown">
      <h3 className="mb-4 text-lg font-semibold text-black dark:text-white">
        {t('your-solutions')}
      </h3>

      {/* Solutions Grid */}
      <div className="grid grid-cols-3 gap-4" data-testid="solutions-grid">
        {solutionItems.map((item) => (
          <DropdownItem
            key={item.label}
            to={item.href || ''}
            imgAlt={`${item.label} Icon`}
            text={item.label}
            icon={item.icon}
            iconType={item.iconType}
            name={item.label}
            isAvailable={item.isAvailable}
            isExternal={'isExternal' in item && !!item.isExternal}
            loopId={isLoopContextActive ? activeLoopId : null}
            loopName={isLoopContextActive ? activeLoopName : null}
            teamspaceId={effectiveTeamspaceId}
          />
        ))}
      </div>

      {/* Loop Home Link */}
      <div
        className="mt-4 border-t border-gray-200 pt-3 text-center dark:border-gray-700"
        id="id-solutions-footer"
        data-testid="solutions-footer"
      >
        <DropdownMenuItem
          id="dropdown-switcher-home"
          onSelect={handleLoopHomeSelect}
          className="flex cursor-pointer items-center justify-center text-lg font-semibold text-gray-500 no-underline transition-colors duration-200 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <img src={loopLogoDarkMode} alt="Loop Home" className="h-10 w-auto" />
        </DropdownMenuItem>
      </div>
    </div>
  );
};

export { DropdownItem };
export default DropdownMenu;
